angular.module('env.threejs.threejsLoader', [])

    // Service to export geometry as file
    .service('threejsLoader', function ($q, $http, $log) {
        var _this = this;

        /**
         * Loaded and evaluated resources
         * @type {string[]}
         */
        var loadedResources = [];

        /**
         * Parse function call
         *
         * @callback parseFunc
         * @param {ArrayBuffer} arrayBuffer
         * @param {Object} loaderInfo
         * @param {string} filename
         */

        /**
         * Supported loader file formats
         * @typedef {Object} LoaderInfo
         * @property {string[]} ext - possible extensions of current format
         * @property {string[]} deps - dependencies (as file URLs) to eval
         * @property {boolean} loaded - Indicates whether files for current Loader was loaded and evaluated
         * @property {parseFunc} parse - Parse function with proper Three.js Loader call
         */

        /**
         * Supported loader file formats
         * Check vendor/three.js/examples/js/loaders for available loaders
         * We list here not all the loaders
         * @type {LoaderInfo[]}
         */
        var loadersInfo = [
            {
                ext: ['stl'],
                deps: ['vendor/three.js/examples/js/loaders/STLLoader.js'],
                parse: function (arrayBuffer, loaderInfo, filename) {
                    return new THREE.STLLoader().parse(arrayBuffer);
                    // returns THREE.BufferGeometry
                }
            },
            {
                // 3D Manufacturing Format
                // https://3mf.io/specification/
                ext: ['3mf'],
                deps: [
                    'vendor/jszip/dist/jszip.min.js',
                    'vendor/three.js/examples/js/loaders/3MFLoader.js'
                ],
                parse: function (arrayBuffer, loaderInfo, filename) {
                    return new THREE.ThreeMFLoader().parse(arrayBuffer);
                    // returns THREE.Group
                }
            },
            {
                // Additive manufacturing file format
                // https://en.wikipedia.org/wiki/Additive_manufacturing_file_format
                ext: ['amf'],
                deps: [
                    'vendor/jszip/dist/jszip.min.js',
                    'vendor/three.js/examples/js/loaders/AMFLoader.js'
                ],
                parse: function (arrayBuffer, loaderInfo, filename) {
                    return new THREE.AMFLoader().parse(arrayBuffer);
                    // returns THREE.Group
                }
            },
            {
                // Away3D Data format
                // Will not work with textures
                // http://awaytools.com/awdformat/
                ext: ['awd'],
                deps: [
                    'vendor/three.js/examples/js/loaders/AWDLoader.js'
                ],
                parse: function (arrayBuffer, loaderInfo, filename) {
                    return new THREE.AWDLoader().parse(arrayBuffer);
                    // returns THREE.Object3D
                }
            },
            {
                // Babylon.js file format (it's JSON actually)
                // https://www.babylonjs.com/
                // https://doc.babylonjs.com/resources/file_format_map_(.babylon)
                ext: ['babylon'],
                deps: [
                    'vendor/three.js/examples/js/loaders/BabylonLoader.js'
                ],
                parse: function (arrayBuffer, loaderInfo, filename) {
                    var decoder = new TextDecoder("utf-8");
                    var str = decoder.decode(new Uint8Array(arrayBuffer));
                    var json = JSON.parse(str);
                    return new THREE.BabylonLoader().parse(json);
                    // returns THREE.Scene
                }
            },


            // BVHLoader.js
            // ColladaLoader.js
            // DDSLoader.js
            // DRACOLoader.js
            // EquirectangularToCubeGenerator.js
            // EXRLoader.js
            // FBXLoader.js
            // GCodeLoader.js
            // GLTFLoader.js
            // HDRCubeTextureLoader.js
            // KMZLoader.js
            // KTXLoader.js
            // LDrawLoader.js
            // LoaderSupport.js
            // MD2Loader.js
            // MMDLoader.js
            // MTLLoader.js
            // NodeMaterialLoader.js
            // NRRDLoader.js
            // OBJLoader.js
            // OBJLoader2.js
            // PCDLoader.js
            // PDBLoader.js
            // PlayCanvasLoader.js
            // PLYLoader.js
            // PRWMLoader.js
            // PVRLoader.js
            // RGBELoader.js
            // STLLoader.js
            // SVGLoader.js
            // TDSLoader.js
            // TGALoader.js
            // TTFLoader.js
            // VRMLLoader.js
            // VRMLoader.js
            // VTKLoader.js
            // XLoader.js
        ];

        _.extend(_this, {
            parseArrayBuffer: parseArrayBuffer,
            parseFile: parseFile
        });

        return _this;

        /**
         * Load 3D file and return geometry
         * @param {File} file
         * @return {Promise} Promise of geometry object
         */
        function parseFile(file) {
            if (file == null || file.size <= 0) {
                return $q.reject("Unable to load file into 3D viewer. File content should not be empty");
            }
            return fileToArrayBuffer(file)
                .then(function(arrayBuffer) {
                    return parseArrayBuffer(arrayBuffer, file.name);
                });
        }

        /**
         * Convert file to ArrayBuffer
         * @param file
         * @return {Promise<ArrayBuffer>}
         */
        function fileToArrayBuffer(file) {
            var deferred = $q.defer();
            var reader = new FileReader();
            reader.onload = function () {
                deferred.resolve(reader.result);
            };
            reader.onerror = function (event) {
                $log.error("Unable to read local file '" + (file
                    ? file.name
                    : "[no file]") + "' as ArrayBuffer", event);
                reader.abort();
                deferred.reject("Unable to read local file as ArrayBuffer");
            };
            reader.readAsArrayBuffer(file);
            return deferred.promise;
        }

        /**
         * Load 3D file contents as ArrayBuffer and return geometry
         * @param {ArrayBuffer} arrayBuffer ArrayBuffer from file
         * @param {string} filename file name to extract extension and detect file format
         * @return {Promise} Promise of geometry object
         */
        function parseArrayBuffer(arrayBuffer, filename) {
            if (arrayBuffer == null || arrayBuffer.byteLength <= 0) {
                return $q.reject("Unable to load file into 3D viewer. File content should not be empty");
            }
            if (!filename) {
                return $q.reject("Unable to load file into 3D viewer. File name should not be empty");
            }

            var loaderInfo = findLoaderByFilename(filename);
            if (!loaderInfo) {
                return $q.reject("Unable to load file into 3D viewer. Format is not supported");
            }
            return ensureLoaderLoaded(loaderInfo)
                .then(function () {
                    try {
                        return loaderInfo.parse(arrayBuffer, loaderInfo, filename);
                    } catch (e) {
                        return $q.reject("Unable to parse file");
                    }
                });
        }

        /**
         * Ensure loader was loaded and evaluated
         * @param loaderInfo
         * @return {Promise}
         */
        function ensureLoaderLoaded(loaderInfo) {
            if (loaderInfo.loaded === true) {
                return $q.when(loaderInfo);
            }
            return loadLoader(loaderInfo);
        }

        /**
         * Load loader js file and eval it
         * @param {LoaderInfo} loaderInfo
         * @return {Promise}
         */
        function loadLoader(loaderInfo) {
            // remove already loaded resources if any
            var notLoadedDeps = _.filter(loaderInfo.deps, function (fileUrl) {
                return !_.contains(loadedResources, fileUrl);
            });

            var promises = _.map(notLoadedDeps, function (fileUrl) {
                return $http.get(fileUrl)
                    .then(function (response) {
                        try {
                            eval(response.data);
                            // $log.debug("[threejsLoader] file loaded and evaluated: " + fileUrl);
                        } catch (e) {
                            $log.error("[threejsLoader] Unable to eval file: " + fileUrl);
                            return $q.reject("Unable to load three.js loader dependency: " + fileUrl);
                        }
                        loadedResources.push(fileUrl);
                    }, function () {
                        $log.error("[threejsLoader] Unable to load loader js file " + fileUrl);
                        return $q.reject("Unable to load three.js loader dependency: " + fileUrl);
                    });
            });

            return $q.all(promises)
                .then(function () {
                    loaderInfo.loaded = true;
                    return loaderInfo;
                }, function (err) {
                    loaderInfo.loaded = false;
                    return $q.reject(err);
                });
        }

        /**
         * Find loader by file extension
         * @param {string} filename
         * @return {Object}
         */
        function findLoaderByFilename(filename) {
            return _.find(loadersInfo, function (loaderInfo) {
                if (!loaderInfo.ext) {
                    return false;
                }
                if (_.isString(loaderInfo.ext)) {
                    return _.string.endsWith(filename.toLowerCase(), loaderInfo.ext.toLowerCase());
                }
                if (_.isArray(loaderInfo.ext)) {
                    return loaderInfo.ext.some(function (e) {
                        return _.string.endsWith(filename.toLowerCase(), e.toLowerCase());
                    });
                }
                return false;
            });
        }
    })

;
